import { createContext, useContext, useEffect, useState } from 'react'

import type { ExperimentClient } from '@amplitude/experiment-js-client'
import Cookies from 'js-cookie'
import type { PropsWithChildren } from 'react'

import { getClient } from '../lib/amplitude'

interface ExperimentContextProps {
  client?: ExperimentClient
  isLoading: boolean
  error?: Error
}

const ExperimentContext = createContext<ExperimentContextProps>({
  isLoading: typeof window !== 'undefined',
})

export const useExperimentsContext = () => {
  const context = useContext(ExperimentContext)

  if (!context) {
    throw new Error('useExperimentsContext must be used within ExperimentProvider')
  }

  return context
}

export const ExperimentProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [client, setClient] = useState<ExperimentClient>()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error>()

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const experimentClient = getClient()

      const analyticsTrackingId = Cookies.get('__atidHelp')

      experimentClient
        ?.start({ user_id: analyticsTrackingId })
        .then(() => {
          setClient(experimentClient)
          setIsLoading(false)
        })
        .catch(err => {
          setError(err instanceof Error ? err : new Error('Failed to initialize experiments'))
          setIsLoading(false)
        })
    }
  }, [])

  return <ExperimentContext.Provider value={{ client, error, isLoading }}>{children}</ExperimentContext.Provider>
}
